/* eslint-disable complexity */
/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { Checkbox, Form, notification, Steps } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import dayjs from 'dayjs';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineArrowLeft, AiOutlineLock } from 'react-icons/ai';
import styled from 'styled-components';

import { CARD_NUMBER_PATTERN, CVC_PATTERN } from '@/utils/card';
import { getCurrencySymbol } from '@/utils/currency';
import { BOOKING_TYPE, Location } from '@core/booking/booking.types';
import { useBookDayPass } from '@core/booking/useBookDayPass';
import { useIsSameExternalDaypassOption } from '@core/settings/actions';
import { useSettingsStore } from '@core/settings/store/settings';
import Button from 'common/atoms/button';
import Dropdown from 'common/atoms/dropdown';
import DynamicLoader from 'common/atoms/dynamic-loader';
import Input from 'common/atoms/input';

import BookingSummary from '../booking-summary';
import ConfigureStep from '../day-pass-booking/ConfigureStep';
import DayPassCheckout from '../day-pass-booking/DayPassCheckout';
import ErrorModal from '../error-modal';
import CheckoutStep from '../meeting-room-booking/CheckoutStep';
import StepProgress from '../meeting-room-booking/StepProgress';
import TimeSlotPicker from '../meeting-room-booking/TimeSlotPicker';
import PackagesForm from '../packages-form';
import PaymentInfoForm from '../payment-info-form';
import NoAvailablePayment from '../payment-info-form/NoAvailablePayment';
import PersonalInfoForm from '../personal-info-form';
import ProcessingPaymentModal from '../processing-payment-modal';
import SuccessModal from '../success-modal';

const StyledButton = styled(Button)`
  border-radius: 16px;
  padding: 0px 40px;
  font-size: 18px;
  font-weight: 400;
  color: var(--main);
  border: 1px solid var(--main);
  background-color: white;
`;

const StyledSteps = styled(Steps)`
  padding: 10px 0px;
  @media (max-width: 425px) {
    .ant-steps-item-title {
      font-size: 12px;
    }
    .ant-steps-item-content {
      width: 100px !important;
    }
  }
`;

// eslint-disable-next-line no-shadow
export enum STEPS_TYPE {
  DAY_PASS = 'day_pass',
  MEETING_ROOM = 'meeting_room',
  PACKAGES = 'packages',
}
interface BookingStepsProps {
  locations: Location[];
  stepsType?: STEPS_TYPE;
  preSelectedLocationId?: string;
  preSelectedRoomId?: string;
  preSelectedDate?: string;
  preSelectedStartTime?: string;
  preSelectedEndTime?: string;
  onBackToLanding?: () => void;
}

// eslint-disable-next-line complexity
const BookingSteps: FC<BookingStepsProps> = ({
  locations,
  stepsType,
  preSelectedLocationId,
  preSelectedRoomId,
  preSelectedDate,
  preSelectedStartTime,
  preSelectedEndTime,
  onBackToLanding,
}) => {
  const { t } = useTranslation();
  const isDayPass = stepsType === STEPS_TYPE.DAY_PASS;
  const isPackages = stepsType === STEPS_TYPE.PACKAGES;
  const isMeetingRoom = stepsType === STEPS_TYPE.MEETING_ROOM;

  const {
    handlePersonalInfoInputChange,
    handlePaymentInfoInputChange,
    selectedDate,
    bookingInfo,
    freePeriods,
    setSelectedDate,
    loadingUnits,
    selectTimeSlot,
    selectedLocationId,
    setSelectedLocationId,
    bookingCost,
    deleteBooking,
    availablePaymentMethods,
    bookMeetingRoom,
    bookMeetingRoomLoading,
    setShowSuccessModal,
    showSuccessModal,
    bookDayPass,
    handleDayPassInfoInputChange,
    handlePaymentTypeChange,
    handleChangeDate,
    dayPassInfo,
    personalInfo,
    paymentInfo,
    errorMsg,
    setBookingType,
    setSelectedUnitId,
    selectedUnitId,
    clearBookingForm,
    currentSelectedLocationUnits,
    allowedOffices,
    availablePackages,
    selectedPackageType,
    setSelectedPackageType,
    selectedPackageId,
    setSelectedPackageId,
    loadingPackages,
    buyPackage,
    applyDiscountCode,
  } = useBookDayPass();
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = useForm();
  const { dayPass: dayPassSettings } = useSettingsStore();
  const isReservedDayPass = useIsSameExternalDaypassOption('reserved');

  // Meeting room specific derived data
  const selectedRoom = useMemo(() => {
    if (!isMeetingRoom || !selectedUnitId) return null;
    return currentSelectedLocationUnits.find((u) => u.id === selectedUnitId) || null;
  }, [isMeetingRoom, selectedUnitId, currentSelectedLocationUnits]);

  const selectedLocation = useMemo(() => {
    return locations.find((l) => l.id === selectedLocationId) || null;
  }, [locations, selectedLocationId]);

  const roomIndex = useMemo(() => {
    if (!selectedRoom) return -1;
    return currentSelectedLocationUnits.findIndex((u) => u.id === selectedRoom.id);
  }, [selectedRoom, currentSelectedLocationUnits]);

  const has12Hours = useMemo(() => {
    return (selectedLocation?.has_12_hours || '1') === '1';
  }, [selectedLocation]);

  const stepBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const BookNow = () => {
    if (paymentInfo.paymentType !== 'credit_card') {
      notification.error({
        message: t('payment.validation.paymentTypeRequired'),
      });
      return;
    }
    form
      .validateFields()
      .then(() => {
        if (isDayPass) bookDayPass();
        else if (isPackages) {
          buyPackage();
        } else if (isMeetingRoom) {
          bookMeetingRoom(selectedLocation);
        }
      })
      .catch(() => {
        notification.error({
          message: t('common.fillRequiredFields'),
        });
      });
  };

  const stepNext = () => {
    if (isPackages && currentStep === 0) {
      if (!selectedPackageId) {
        notification.error({
          message: t('bookingSteps.selectPackageFirst'),
        });
      } else {
        setCurrentStep(currentStep + 1);
      }
    } else if (isMeetingRoom || isPackages) {
      form
        .validateFields()
        .then(() => {
          setCurrentStep(currentStep + 1);
        })
        .catch(() => {
          notification.error({
            message: t('common.fillRequiredFields'),
          });
        });
    }
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    clearBookingForm();
    form.resetFields();
    setCurrentStep(0);
  };

  const hasPrePopulated = useRef(false);
  const hasInitialized = useRef(false);

  // Runs again when the locations store populates: on a direct deep link
  // (e.g. /booking/meeting-room?...&locationId=1) locations often arrive
  // after mount, and a mount-only effect would leave the page stuck on the
  // availability loader forever.
  useEffect(() => {
    if (hasInitialized.current || locations.length === 0) return;
    hasInitialized.current = true;
    setBookingType(isDayPass ? BOOKING_TYPE.DAY_PASS : BOOKING_TYPE.MEETING_ROOM);
    const initialLocationId = preSelectedLocationId || locations[0].id;
    setSelectedLocationId(initialLocationId);
    form.setFieldsValue({ location: initialLocationId });
    if (preSelectedDate) {
      setSelectedDate(dayjs(preSelectedDate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  // Pre-select room after units have loaded for the location
  useEffect(() => {
    if (preSelectedRoomId && currentSelectedLocationUnits.length > 0) {
      if (currentSelectedLocationUnits.some((u) => u.id === preSelectedRoomId)) {
        setSelectedUnitId(preSelectedRoomId);
      }
    }
  }, [currentSelectedLocationUnits, preSelectedRoomId]);

  // Pre-populate time slot from calendar view query params
  useEffect(() => {
    if (hasPrePopulated.current) return;
    if (!preSelectedStartTime || !preSelectedEndTime) return;
    if (roomIndex < 0 || !freePeriods?.[roomIndex]) return;

    const start = Number(preSelectedStartTime);
    const end = Number(preSelectedEndTime);
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return;

    // Check that the start time is available in freePeriods
    if (!freePeriods[roomIndex][start]) return;

    hasPrePopulated.current = true;
    selectTimeSlot(roomIndex, start, end - start, has12Hours);
  }, [freePeriods, roomIndex, preSelectedStartTime, preSelectedEndTime, has12Hours, selectTimeSlot]);

  useEffect(() => {
    const outerScroll = document.getElementById('outer-scroll');
    if (outerScroll) {
      outerScroll.scrollTo(0, 0);
    }
  }, [currentStep]);

  // Meeting room step navigation — mobile only (desktop is single-page with Pay button)
  const meetingRoomStepNext = useCallback(() => {
    if (currentStep === 0) {
      if (!bookingInfo.toTime || !bookingInfo.conferenceRoom) {
        notification.error({ message: t('bookingSteps.selectTimeSlot') });
        return;
      }
      setCurrentStep(1);
    } else if (currentStep === 1) {
      form
        .validateFields()
        .then(() => setCurrentStep(2))
        .catch(() => notification.error({ message: t('common.fillRequiredFields') }));
    }
  }, [currentStep, bookingInfo, form, t]);

  const meetingRoomStepBack = useCallback(() => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  // ── Meeting Room Flow ──
  if (isMeetingRoom) {
    const durationMinutes = bookingInfo.toTime - bookingInfo.fromTime;
    const durationHours = Number((durationMinutes / 60).toFixed(durationMinutes % 60 === 0 ? 0 : 1));
    const durationLabel =
      durationMinutes > 0
        ? durationMinutes < 60
          ? t('common.minutesShort', { count: durationMinutes })
          : t('common.hoursShort', { count: durationHours })
        : '';

    // Compute applicable bulk discount
    const appliedDiscount = useMemo(() => {
      if (!selectedRoom?.bulk_discounts || durationMinutes <= 0) return null;
      try {
        const discounts = JSON.parse(selectedRoom.bulk_discounts) as { hours: number; percentage: number }[];
        if (!Array.isArray(discounts)) return null;
        const durationHours = durationMinutes / 60;
        // Find the best matching tier (highest hours threshold that the duration meets)
        const applicable = discounts.filter((d) => durationHours >= d.hours).sort((a, b) => b.hours - a.hours);
        return applicable.length > 0 ? applicable[0] : null;
      } catch {
        return null;
      }
    }, [selectedRoom?.bulk_discounts, durationMinutes]);

    const hasCreditCard = availablePaymentMethods.some((m) => m === 'credit_card');
    const hideZipAndAddress = selectedLocation?.countryCode === 'IL';
    const totalText = bookingCost
      ? `${getCurrencySymbol(bookingCost.currency, selectedLocation)}${bookingCost.total.toFixed(2)}`
      : '';

    // Mobile: 3 steps.
    const mobileSteps = [
      t('bookingSteps.mobileSteps.selectTime'),
      t('bookingSteps.mobileSteps.yourInfo'),
      t('bookingSteps.mobileSteps.payment'),
    ];

    return (
      <div
        style={{ zoom: '100%' }}
        id="outer-scroll"
        className="mx-2 w-full no-scrollbar overflow-y-auto overflow-x-hidden"
      >
        {/* Step Progress — mobile only (desktop is single-page) */}
        <div className="md:hidden">
          <StepProgress currentStep={currentStep} steps={mobileSteps} />
        </div>

        {/* Main Content Card */}
        <div className="w-full bg-white rounded-2xl p-4 md:p-6 relative" style={{ minHeight: 400 }}>
          {/* Top Bar: Back + Continue (Continue only on mobile) */}
          <div className="flex items-center justify-between mb-4">
            <div>
              {(!!currentStep || (currentStep === 0 && onBackToLanding)) && (
                <button
                  type="button"
                  className="flex items-center gap-2 px-4 py-2 rounded-full cursor-pointer border-none"
                  style={{ backgroundColor: '#F3F4F6', color: '#404D61' }}
                  onClick={currentStep === 0 && onBackToLanding ? onBackToLanding : meetingRoomStepBack}
                >
                  <AiOutlineArrowLeft size={14} className="rtl:rotate-180" />
                  <span className="text-sm font-medium">{t('common.back')}</span>
                </button>
              )}
            </div>
            {/* Continue button — mobile only */}
            {currentStep !== 2 && (
              <button
                type="button"
                onClick={meetingRoomStepNext}
                className="md:hidden px-8 py-3 rounded-xl text-base font-semibold border-none cursor-pointer"
                style={{
                  backgroundColor: bookingInfo.toTime ? 'var(--main)' : '#E5E7EB',
                  color: bookingInfo.toTime ? '#FFFFFF' : '#9CA3AF',
                }}
              >
                {t('bookingSteps.continue')}
              </button>
            )}
          </div>

          {/* Step 0: Time Selection — desktop shows personal info alongside */}
          {currentStep === 0 && (
            <>
              {loadingUnits || !selectedRoom ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <DynamicLoader size="large" />
                  <p className="mt-4 text-sm" style={{ color: '#707070' }}>
                    {t('bookingSteps.loadingAvailableTimes')}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Left: Time Selection (wider) */}
                  <div className="min-w-0" style={{ flex: '3 1 0%' }}>
                    <TimeSlotPicker
                      room={selectedRoom}
                      location={selectedLocation}
                      selectedDate={selectedDate}
                      setSelectedDate={setSelectedDate}
                      freePeriods={freePeriods}
                      roomIndex={roomIndex}
                      has12Hours={has12Hours}
                      bookingCost={bookingCost}
                      selectedStartTime={bookingInfo.fromTime || null}
                      selectedDuration={durationMinutes > 0 ? durationMinutes : null}
                      onSelectTimeSlot={selectTimeSlot}
                      onDeleteBooking={deleteBooking}
                      appliedDiscount={appliedDiscount}
                      onApplyDiscountCode={applyDiscountCode}
                    />
                  </div>

                  {/* Right: Details + Payment — desktop only */}
                  <div className="hidden md:block min-w-0 overflow-y-auto" style={{ flex: '2 1 0%' }}>
                    {!hasCreditCard ? (
                      <NoAvailablePayment />
                    ) : (
                      <Form form={form} layout="vertical" className="w-full">
                        {/* Personal Info */}
                        <h2 className="text-base font-bold m-0 mb-3" style={{ color: '#404D61' }}>
                          {t('bookingSteps.yourDetails')}
                        </h2>
                        <PersonalInfoForm
                          form={form}
                          personalInfo={personalInfo}
                          handlePersonalInfoInputChange={handlePersonalInfoInputChange}
                          compact
                          noFormWrapper
                        />

                        {/* Divider */}
                        <div className="my-4" style={{ borderTop: '1px solid #E5E7EB' }} />

                        {/* Payment Info */}
                        <h2 className="text-base font-bold m-0 mb-3" style={{ color: '#404D61' }}>
                          {t('bookingSteps.paymentDetails')}
                        </h2>

                        {/* Row 1: Payment Type + Card Number + Name on Card */}
                        <div className="grid grid-cols-3 gap-x-3">
                          <Form.Item
                            initialValue={paymentInfo.paymentType}
                            name="paymentType"
                            label={t('payment.paymentType')}
                            rules={[{ required: true, message: t('common.required') }]}
                            style={{ marginBottom: 12 }}
                          >
                            <Dropdown
                              onChange={handlePaymentTypeChange}
                              options={[{ value: 'credit_card', label: t('payment.creditCard') }]}
                              name="Payment Type"
                            />
                          </Form.Item>

                          <Form.Item
                            initialValue={paymentInfo.cardNumber}
                            name="cardNumber"
                            label={t('payment.cardNumber')}
                            rules={[
                              { required: true, message: t('common.required') },
                              {
                                pattern: CARD_NUMBER_PATTERN,
                                message: t('payment.validation.invalidCard'),
                              },
                            ]}
                            style={{ marginBottom: 12 }}
                          >
                            <Input
                              onChange={handlePaymentInfoInputChange}
                              name="cardNumber"
                              placeholder={t('payment.placeholders.cardNumber')}
                            />
                          </Form.Item>

                          <Form.Item
                            initialValue={paymentInfo.nameOnTheCard}
                            name="nameOnTheCard"
                            label={t('payment.nameOnCard')}
                            rules={[
                              { required: true, message: t('common.required') },
                              { pattern: /^[\p{L}\p{M}\s'’.-]{2,30}$/u, message: t('payment.validation.invalidName') },
                            ]}
                            style={{ marginBottom: 12 }}
                          >
                            <Input
                              onChange={handlePaymentInfoInputChange}
                              name="nameOnTheCard"
                              placeholder={t('payment.placeholders.nameOnCard')}
                            />
                          </Form.Item>
                        </div>

                        {/* Row 2: Expiry + CVC + ZIP (or just Expiry + CVC if hideZipAndAddress) */}
                        <div className={`grid gap-x-3 ${hideZipAndAddress ? 'grid-cols-2' : 'grid-cols-3'}`}>
                          <Form.Item label={t('payment.expirationDate')} required style={{ marginBottom: 12 }}>
                            <div className="flex items-center gap-1" dir="ltr">
                              <Form.Item
                                initialValue={paymentInfo.expireDateMonth}
                                name="expireDateMonth"
                                noStyle
                                rules={[
                                  { required: true, message: t('payment.validation.month') },
                                  { pattern: /^(0[1-9]|1[0-2])$/, message: t('payment.validation.invalid') },
                                ]}
                              >
                                <Input
                                  onChange={handlePaymentInfoInputChange}
                                  name="expireDateMonth"
                                  placeholder={t('payment.placeholders.mm')}
                                />
                              </Form.Item>
                              <span style={{ color: '#9CA3AF' }}>/</span>
                              <Form.Item
                                initialValue={paymentInfo.expireDateYear}
                                name="expireDateYear"
                                noStyle
                                rules={[
                                  { required: true, message: t('payment.validation.year') },
                                  { pattern: /^(2[3-9]|3[0-9])$/, message: t('payment.validation.invalid') },
                                ]}
                              >
                                <Input
                                  onChange={handlePaymentInfoInputChange}
                                  name="expireDateYear"
                                  placeholder={t('payment.placeholders.yy')}
                                />
                              </Form.Item>
                            </div>
                          </Form.Item>

                          <Form.Item
                            initialValue={paymentInfo.cvv}
                            name="cvc_cvv"
                            label={t('payment.cvc')}
                            rules={[
                              { required: true, message: t('common.required') },
                              { pattern: CVC_PATTERN, message: t('payment.validation.invalid') },
                            ]}
                            style={{ marginBottom: 12 }}
                          >
                            <Input
                              onChange={handlePaymentInfoInputChange}
                              name="cvv"
                              placeholder={t('payment.placeholders.cvc')}
                            />
                          </Form.Item>

                          {!hideZipAndAddress && (
                            <Form.Item
                              initialValue={paymentInfo.zip}
                              name="ZIP"
                              label={t('payment.zipCode')}
                              rules={[
                                { required: true, message: t('common.required') },
                                { pattern: /^\d{5}$/, message: t('payment.validation.invalid') },
                              ]}
                              style={{ marginBottom: 12 }}
                            >
                              <Input
                                onChange={handlePaymentInfoInputChange}
                                name="zip"
                                placeholder={t('payment.placeholders.zip')}
                              />
                            </Form.Item>
                          )}
                        </div>

                        {/* Billing Address — full width */}
                        {!hideZipAndAddress && (
                          <Form.Item
                            initialValue={paymentInfo.address}
                            name="billingAddress"
                            label={t('payment.billingAddress')}
                            rules={[{ required: true, message: t('common.required') }]}
                            style={{ marginBottom: 12 }}
                          >
                            <Input
                              onChange={handlePaymentInfoInputChange}
                              name="address"
                              placeholder={t('payment.billingAddress')}
                            />
                          </Form.Item>
                        )}

                        {/* Save CC Checkbox */}
                        <Checkbox
                          checked={paymentInfo.save_cc}
                          onChange={(e) => {
                            handlePaymentInfoInputChange({
                              target: { name: 'save_cc', value: e.target.checked },
                            } as any);
                          }}
                          style={{ marginBottom: 12 }}
                        >
                          <span className="text-sm" style={{ color: '#404D61' }}>
                            {t('payment.saveCard')}
                          </span>
                        </Checkbox>

                        {/* Security Badge */}
                        <div className="flex items-center gap-2 mt-1 mb-3">
                          <AiOutlineLock size={14} style={{ color: 'var(--main)' }} />
                          <span className="text-xs font-medium" style={{ color: 'var(--main)' }}>
                            {t('payment.tlsNotice')}
                          </span>
                        </div>

                        {/* Pay Button */}
                        <button
                          type="button"
                          onClick={BookNow}
                          disabled={bookMeetingRoomLoading}
                          className="w-full py-3 rounded-xl text-white text-base font-semibold border-none cursor-pointer transition-opacity"
                          style={{
                            backgroundColor: bookingInfo.toTime ? 'var(--main)' : '#E5E7EB',
                            color: bookingInfo.toTime ? '#FFFFFF' : '#9CA3AF',
                            opacity: bookMeetingRoomLoading ? 0.7 : 1,
                          }}
                        >
                          {bookMeetingRoomLoading
                            ? t('payment.processing')
                            : t('payment.payAmount', { amount: totalText })}
                        </button>
                      </Form>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Step 1: Personal Info — mobile only */}
          {currentStep === 1 && (
            <>
              <h2 className="text-xl font-bold m-0 mb-6" style={{ color: '#404D61' }}>
                {t('bookingSteps.yourDetails')}
              </h2>
              <PersonalInfoForm
                form={form}
                personalInfo={personalInfo}
                handlePersonalInfoInputChange={handlePersonalInfoInputChange}
                compact
              />
            </>
          )}

          {/* Step 2: Checkout */}
          {currentStep === 2 && (
            <CheckoutStep
              room={selectedRoom}
              location={selectedLocation}
              bookingCost={bookingCost}
              startTime={bookingInfo.uiPosition.startTime}
              endTime={bookingInfo.uiPosition.endTime}
              dateLabel={selectedDate.format('dddd, MMMM D, YYYY')}
              durationLabel={durationLabel}
              personalInfo={personalInfo}
              form={form}
              paymentInfo={paymentInfo}
              handlePaymentInfoInputChange={handlePaymentInfoInputChange}
              handlePaymentTypeChange={handlePaymentTypeChange}
              availablePaymentMethods={availablePaymentMethods}
              hideZipAndAddress={selectedLocation?.countryCode === 'IL'}
              onPay={BookNow}
              loading={bookMeetingRoomLoading}
              onApplyDiscountCode={applyDiscountCode}
            />
          )}
        </div>

        <ErrorModal errorMsg={errorMsg} />
        <SuccessModal closeModal={closeSuccessModal} visible={showSuccessModal} />
        <ProcessingPaymentModal visible={bookMeetingRoomLoading} />
      </div>
    );
  }

  // ── Day Pass Flow ──
  if (isDayPass) {
    const selectedOffice = allowedOffices.find((office) => office.id === selectedUnitId) || null;
    let dayCount = dayPassInfo.dates.length;
    if (isReservedDayPass) {
      dayCount =
        dayPassInfo.startDate && dayPassInfo.endDate
          ? dayjs(dayPassInfo.endDate).diff(dayPassInfo.startDate, 'day') + 1
          : 0;
    }
    const perDayPrice = isReservedDayPass
      ? selectedOffice?.daypass_price || selectedLocation?.daypass_price || ''
      : selectedLocation?.daypass_price || '';
    const canContinue = isReservedDayPass
      ? !!(dayPassInfo.startDate && dayPassInfo.endDate && selectedUnitId)
      : dayPassInfo.dates.length >= 1;

    // Toggle a flex-mode day on/off. The quantity is written last (as a
    // string — that's what the Input-driven hook state expects): the hook's
    // quantity effect then reconciles the dates array, trimming the stale
    // tail on removal and adding nothing on append since lengths match.
    const toggleDayPassDate = (d: string) => {
      const { dates } = dayPassInfo;
      if (dates.includes(d)) {
        if (dates.length === 1) return;
        const next = dates.filter((x) => x !== d);
        next.forEach((x, i) => handleChangeDate(i, x));
        handleDayPassInfoInputChange({ target: { name: 'quantity', value: String(next.length) } } as any);
      } else {
        handleChangeDate(dates.length, d);
        handleDayPassInfoInputChange({ target: { name: 'quantity', value: String(dates.length + 1) } } as any);
      }
    };

    const handleDayPassRangeChange = (startDate: string | null, endDate: string | null) => {
      handleDayPassInfoInputChange({ target: { name: 'startDate', value: startDate } } as any);
      handleDayPassInfoInputChange({ target: { name: 'endDate', value: endDate } } as any);
    };

    const dayPassContinue = () => {
      if (isReservedDayPass) {
        form
          .validateFields(['location', 'dateRange', 'office'])
          .then(() => setCurrentStep(1))
          .catch(() => notification.error({ message: t('common.fillRequiredFields') }));
      } else if (dayPassInfo.dates.length >= 1) {
        setCurrentStep(1);
      }
    };

    // Mobile step 1 → 2: validate only the personal-info fields (payment
    // fields are mounted but CSS-hidden on this stage).
    const dayPassInfoNext = () => {
      form
        .validateFields(['name', 'email', 'company', 'phone'])
        .then(() => setCurrentStep(2))
        .catch(() => notification.error({ message: t('common.fillRequiredFields') }));
    };

    const mobileSteps = [
      t('dayPass.steps.chooseDates'),
      t('bookingSteps.mobileSteps.yourInfo'),
      t('bookingSteps.mobileSteps.payment'),
    ];

    return (
      <div
        style={{ zoom: '100%' }}
        id="outer-scroll"
        className="mx-2 w-full no-scrollbar overflow-y-auto overflow-x-hidden"
      >
        {/* Step Progress — mobile only (desktop is two screens with its own CTA) */}
        <div className="md:hidden">
          <StepProgress currentStep={currentStep} steps={mobileSteps} />
        </div>

        <div className="w-full bg-white rounded-2xl p-4 md:p-6 relative" style={{ minHeight: 400 }}>
          {/* Top Bar: Back pill + mobile Continue (personal-info step only) */}
          <div className="flex items-center justify-between mb-4">
            <div>
              {(!!currentStep || (currentStep === 0 && onBackToLanding)) && (
                <button
                  type="button"
                  className="flex items-center gap-2 px-4 py-2 rounded-full cursor-pointer border-none"
                  style={{ backgroundColor: '#F3F4F6', color: '#404D61' }}
                  onClick={currentStep === 0 && onBackToLanding ? onBackToLanding : stepBack}
                >
                  <AiOutlineArrowLeft size={14} className="rtl:rotate-180" />
                  <span className="text-sm font-medium">{t('common.back')}</span>
                </button>
              )}
            </div>
            {currentStep === 1 && (
              <button
                type="button"
                onClick={dayPassInfoNext}
                className="md:hidden px-8 py-3 rounded-xl text-base font-semibold border-none cursor-pointer"
                style={{ backgroundColor: 'var(--main)', color: '#FFFFFF' }}
              >
                {t('bookingSteps.continue')}
              </button>
            )}
          </div>

          {currentStep === 0 && (
            <ConfigureStep
              form={form}
              dayPassSettings={dayPassSettings}
              locations={locations}
              selectedLocation={selectedLocation}
              selectedLocationId={selectedLocationId}
              setSelectedLocationId={setSelectedLocationId}
              isReserved={isReservedDayPass}
              dayPassInfo={dayPassInfo}
              onToggleDate={toggleDayPassDate}
              onRemoveDate={toggleDayPassDate}
              onRangeChange={handleDayPassRangeChange}
              allowedOffices={allowedOffices}
              selectedUnitId={selectedUnitId}
              setSelectedUnitId={setSelectedUnitId}
              bookingCost={bookingCost}
              dayCount={dayCount}
              perDayPrice={perDayPrice}
              onContinue={dayPassContinue}
              canContinue={canContinue}
            />
          )}

          {currentStep >= 1 && (
            <DayPassCheckout
              dayPassSettings={dayPassSettings}
              location={selectedLocation}
              isReserved={isReservedDayPass}
              dayPassInfo={dayPassInfo}
              dayCount={dayCount}
              officeName={selectedOffice?.name || null}
              personalInfo={personalInfo}
              bookingCost={bookingCost}
              mobileStage={currentStep === 1 ? 'info' : 'payment'}
              form={form}
              paymentInfo={paymentInfo}
              handlePersonalInfoInputChange={handlePersonalInfoInputChange}
              handlePaymentInfoInputChange={handlePaymentInfoInputChange}
              handlePaymentTypeChange={handlePaymentTypeChange}
              availablePaymentMethods={availablePaymentMethods}
              hideZipAndAddress={selectedLocation?.countryCode === 'IL'}
              onPay={BookNow}
              loading={bookMeetingRoomLoading}
            />
          )}
        </div>

        <ErrorModal errorMsg={errorMsg} />
        <SuccessModal closeModal={closeSuccessModal} visible={showSuccessModal} />
        <ProcessingPaymentModal visible={bookMeetingRoomLoading} />
      </div>
    );
  }

  // ── Packages Flow (legacy layout) ──
  return (
    <div
      style={{ zoom: '100%' }}
      id="outer-scroll"
      className="mx-2 w-full no-scrollbar overflow-y-scroll overflow-x-hidden"
    >
      <p className="text-4xl font-bold m-0 mt-5" style={{ color: '#404D61' }}>
        {t('bookingSteps.buyPackage')}
      </p>
      <p
        style={{
          fontFamily: 'Product Sans',
          fontStyle: 'normal',
          fontWeight: '400',
          fontSize: '18px',
          lineHeight: '24px',
          color: '#707070',
        }}
      >
        {t('bookingSteps.enterYourInformation')}
      </p>
      <div className="flex flex-col justify-between align-top h-auto mt-0 md:mt-10">
        <div className="overflow-hidden p-0 flex justify-start ">
          <StyledSteps
            responsive={false}
            direction="horizontal"
            progressDot={(_iconDot, { status }) => {
              return (
                <div
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '10px',
                    border: '1px solid',
                    color: status === 'wait' ? 'rgba(0, 0, 0, 0.45)' : 'var(--main)',
                    backgroundColor: status === 'wait' ? 'white' : 'var(--main)',
                  }}
                />
              );
            }}
            current={currentStep}
            items={[
              {
                title: t('bookingSteps.packageInfo'),
              },
              {
                title: t('bookingSteps.personalInfo'),
              },
              {
                title: t('bookingSteps.paymentInfo'),
              },
            ]}
            size="small"
          />
        </div>
        <div className="w-full md:w-6/6 m-auto md:ms-0 md:me-10 mt-3 md:mt-0 relative p-3 pt-3 md:pt-10 pb:3 md:pb-14 bg-white rounded-3xl h-auto md:h-4/5">
          {(!!currentStep || (currentStep === 0 && onBackToLanding)) && (
            <div
              className="flex flex-row absolute top-0 md:top-10 justify-center items-center px-4 py-2 rounded-full cursor-pointer"
              style={{
                background: '#F8F8F8',
                insetInlineStart: 0,
              }}
              onClick={currentStep === 0 && onBackToLanding ? onBackToLanding : stepBack}
            >
              <AiOutlineArrowLeft className="rtl:rotate-180" />
            </div>
          )}
          {currentStep === 1 && (
            <PersonalInfoForm
              form={form}
              personalInfo={personalInfo}
              handlePersonalInfoInputChange={handlePersonalInfoInputChange}
            />
          )}

          {currentStep === 0 && (
            <div className="p-0 pb-8">
              {isPackages && (
                <PackagesForm
                  loadingPackages={loadingPackages}
                  form={form}
                  availablePackages={availablePackages}
                  selectedLocationId={selectedLocationId}
                  setSelectedLocationId={setSelectedLocationId}
                  locations={locations}
                  selectedPackageType={selectedPackageType}
                  setSelectedPackageType={setSelectedPackageType}
                  selectedPackageId={selectedPackageId}
                  setSelectedPackageId={setSelectedPackageId}
                />
              )}
            </div>
          )}
          {currentStep === 2 && (
            <PaymentInfoForm
              form={form}
              handlePaymentTypeChange={handlePaymentTypeChange}
              paymentInfo={paymentInfo}
              hideZipAndAddress={locations.find((location) => location.id === selectedLocationId)?.countryCode === 'IL'}
              availablePaymentMethods={availablePaymentMethods}
              handlePaymentInfoInputChange={handlePaymentInfoInputChange}
            />
          )}
          <div
            className="flex justify-between md:mt-0 relative bottom-9 md:top-12 end-0 md:end-6"
            style={{ backgroundColor: 'white', borderRadius: '16px', padding: '16px', zIndex: 1 }}
          >
            <div />
            {currentStep !== 2 ? (
              <StyledButton onClick={stepNext}>{t('common.next')}</StyledButton>
            ) : (
              <StyledButton onClick={BookNow}>{t('bookingSteps.book')}</StyledButton>
            )}
          </div>
          <div className="relative bottom-16">
            <BookingSummary
              selectedLocation={locations.find((location) => location.id === selectedLocationId)}
              bookingCost={bookingCost}
            />
          </div>
        </div>

        <ErrorModal errorMsg={errorMsg} />
        <SuccessModal closeModal={closeSuccessModal} visible={showSuccessModal} />
        <ProcessingPaymentModal visible={bookMeetingRoomLoading} />
      </div>
    </div>
  );
};

export default BookingSteps;
